import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";

import { IGiveaway, IGiveawayHistory, IPanelUser, IRulesGiveaway } from "../../../interfaces";
import Text from "antd/es/typography/Text";
import { EmitError } from "../../../utils/EmitError";
import GiveawaysService from "../../../services/http/services/GiveawaysService";
import PanelUsersService from "../../../services/http/services/PanelUsersService";
import { EGiveawayStatus } from "../../../enums";
import { useGetColumnSearchPropsData } from "../../../hooks/FilterTable/date";
import { formatDateStringToDateAndTime } from "../../../utils/Conversores";
import { SortOrder } from "antd/es/table/interface";
import { useGetColumnSearchProps } from "../../../hooks/FilterTable/stringClearFiltros";
import { Button, message, Space, Tag, Tooltip } from "antd";
import { FaCopy, FaDownload, FaUsers } from "react-icons/fa";
import { EActionType, useActionsTable } from "../../../hooks/ActionsTable";
import AwardsService from "../../../services/http/services/AwardsService";
import { IAwardWithWinner } from "../HoldGiveaway/Steps/ImportData/useImportData";
import useAuth from "../../../hooks/useAuth";


export interface ITableData extends IGiveawayHistory {
    key: number | undefined;
}


export default function useVerifyGiveaway() {
    const [giveaway, setGiveaway] = useState<IGiveaway | undefined>();
    const [rulesGiveaway, setRulesGiveaway] = useState<IRulesGiveaway | undefined>();
    const [isDownloadFile, setIsDownloadingFile] = useState<boolean>();
    const [isLoadingGiveaway, setIsLoadingGiveaway] = useState<boolean>(true);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [showWinners, setShowWinners] = useState<boolean>(false);
    const [awardsWithWinners, setAwardsWithWinners] = useState<IAwardWithWinner[] | undefined>(undefined);
    const navigate = useNavigate();
    const [panelUserGeneratedParcitipantsFile, setPanelUserGeneratedParticipantsFile] = useState<IPanelUser | undefined>();
    const { id } = useParams();
    const { activeCity } = useAuth();

    const copyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            message.success("Hash copiado!");
        } catch {
            message.error("Erro ao copiar hash.");
        }
    };

    const lastGiveawayHistory = useMemo(() => {

        if (!giveaway) {
            return;
        }
        return giveaway.raffle_history && giveaway.raffle_history[giveaway.raffle_history.length - 1]
    }, [giveaway]);

    const alreadyGeneratedTicketsFile = useMemo(() => {
        if (!lastGiveawayHistory) {
            return false;
        }

        return Boolean(lastGiveawayHistory?.hash_with_participants);
    }, [lastGiveawayHistory]);


    const enableResultStep = giveaway && (giveaway.status !== EGiveawayStatus.FINISHED && giveaway.status !== EGiveawayStatus.UNDER_VERIFICATION);

    const handleCloseModalWinners = useCallback(() => {
        setShowWinners(false);
    }, []);


    const handleClickExportStep = useCallback(() => {
        setCurrentStep(0);
    }, []);

    const handleClickImportStep = useCallback(() => {
        setCurrentStep(1);
    }, []);

    const handleClickResultStep = useCallback(() => {
        setCurrentStep(2);
    }, []);

    const handleClickViewAwards = useCallback(() => {
        navigate('/sorteios/geral/visualizar/' + giveaway?.id + '?tab=5');
    }, [giveaway, navigate]);

    const handleClickNextStep = useCallback(() => {
        setCurrentStep((prevState) => prevState < 2 ? prevState + 1 : prevState);
    }, []);

    const handleClickPreviousStep = useCallback(() => {
        setCurrentStep((prevState) => prevState > 0 ? prevState - 1 : prevState);
    }, []);

    const setTableDataFromRequest = useCallback((data: IGiveaway) => {
        if (!data.raffle_history) {
            return;
        }

        const newData = data.raffle_history.map((history) => ({
            ...history,
            key: history.id,
            observation: `${history.attempt_number ? history.attempt_number + '° ' : ''} ` + history.observation,
        }));
        setTableData(newData);
    }, []);

    const loadGiveaway = useCallback(async (signal: AbortSignal) => {
        try {
            setIsLoadingGiveaway(true);

            const giveawayData = await GiveawaysService.getGiveawayById({
                id: Number(id),
                signal,
            });

            setGiveaway(giveawayData);
            setTableDataFromRequest(giveawayData);
            setIsLoadingGiveaway(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }
            EmitError(err);
        } finally {
            setIsLoadingGiveaway(false);
        }
    }, [id, setTableDataFromRequest]);

    const loadRulesGiveaway = useCallback(async (signal: AbortSignal) => {
        try {
            if (!activeCity?.id) {
                return;
            }

            const rulesGiveawayData = await GiveawaysService.getRulesGiveawayByCountyId({
                county_id: activeCity.id,
                signal,
            });

            setRulesGiveaway(rulesGiveawayData);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }
            EmitError(err);
        }
    }, [activeCity]);

    const handleTicketsFileGenerated = useCallback((hash: string, file: string) => {
        const controller = new AbortController();

        loadGiveaway(controller.signal);

        return () => {
            controller.abort();
        }
    }, [loadGiveaway]);

    const refreshGiveaway = useCallback(async () => {


        const controller = new AbortController();

        await loadGiveaway(controller.signal);

    }, [loadGiveaway]);

    const handleClickDownloadTicketsFile = useCallback(async (file: string) => {


        const controller = new AbortController();

        try {
            setIsDownloadingFile(true);
            const responseFile = await GiveawaysService.downloadGiveawayTicketsFile({ fileName: file, signal: controller.signal });

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(responseFile);
            link.download = file;
            link.click();
            window.URL.revokeObjectURL(link.href);
            setIsDownloadingFile(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            setIsDownloadingFile(false);
            EmitError(err, true, 'holdGiveawayExportData_ajksdkasdjasjdasd')
        }

        return () => {
            controller.abort();
        }
    }, []);

    const getWinnersByGiveawayAndAttempt = useCallback(async (attempt_number: number) => {
        if (!giveaway) {
            return;
        }

        const controller = new AbortController();

        try {
            const awards = await AwardsService.getAwardWinnersByGiveawayIdAndAttemptNumber({ giveawayId: giveaway.id, attemptNumber: attempt_number, signal: controller.signal });


            const awardsWithTicketWinner = awards.sort((a, b) => a.order - b.order)?.map((award) => {

                return {
                    id: award.id,
                    description: award.description,
                    winner: award.winner,
                    images: award.images,
                }
            });
            setAwardsWithWinners(awardsWithTicketWinner);

            if (awardsWithTicketWinner)
                setShowWinners(true);

        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            EmitError(err, true, 'holdGiveawayImportData_asdasdasdasd')
        }

        return () => {
            controller.abort();
        }
    }, [giveaway]);

    const getOneWinnerByGiveawayAwardAndAttempt = useCallback(async (awardId: number, attempt_number: number) => {
        if (!giveaway) {
            return;
        }

        const controller = new AbortController();

        try {
            const awards = await AwardsService.getAwardWinnersByGiveawayIdAndAttemptNumber({ giveawayId: giveaway.id, attemptNumber: attempt_number, signal: controller.signal });

            const award = awards.sort((a, b) => a.order - b.order)?.find((data) => data.id === awardId);

            if (!award) {
                return null;
            }

            const awardsWithTicketWinner = {
                id: award.id,
                description: award.description,
                winner: award.winner,
                images: award.images,
            }
            setAwardsWithWinners([awardsWithTicketWinner]);

            if (awardsWithTicketWinner)
                setShowWinners(true);

        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            EmitError(err, true, 'holdGiveawayImportData_asdasdasdasd')
        }

        return () => {
            controller.abort();
        }
    }, [giveaway]);

    const columns = [
        {
            title: 'Data',
            dataIndex: 'created_at',
            key: 'created_at',
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({ dataIndex: 'created_at' }),
            render: (data: any) => (data ? formatDateStringToDateAndTime(data) : ''),
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITableData, b: ITableData) => new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime(),
            width: '15%'
        },
        {
            title: 'Observação',
            dataIndex: 'observation',
            key: 'observation',
            ...useGetColumnSearchProps({ dataIndex: 'observation', ref: refFuncaoLimparFiltrosString }),
        },
        {
            title: 'Hash',
            dataIndex: 'hash',
            key: 'hash',
            render: (_: any, history: ITableData) => {
                const isSorteioRealizado =
                    Boolean(history.observation?.includes('Sorteio realizado!')) ||
                    Boolean((history as any).status?.includes('Sorteio realizado!')) ||
                    Boolean(history.observation?.toLowerCase().includes('sorteio realizado'));
                const showParticipantsHash =
                    Boolean(history.hash_with_participants) && !isSorteioRealizado;

                return (<Space
                    direction="vertical"
                    size="small"
                >
                    {showParticipantsHash && (
                        <Space align="start" direction="horizontal" size={4}>
                            <Text type="secondary" strong style={{ fontSize: 12 }}>Participantes:</Text>
                            <Tag color="blue" style={{ marginLeft: 2 }}>
                                {history.hash_with_participants}
                            </Tag>
                            <Tooltip title="Copiar hash">
                                <Button
                                    icon={<FaCopy color="#999" />}
                                    size="small"
                                    onClick={() =>
                                        copyToClipboard(history.hash_with_participants!)
                                    }
                                />
                            </Tooltip>
                            {history.file_with_participants && <Tooltip title="Baixar arquivo de participantes">
                                <Button
                                    icon={<FaDownload />}
                                    size="small"
                                    loading={isDownloadFile}
                                    onClick={() =>
                                        history.file_with_participants && handleClickDownloadTicketsFile(history.file_with_participants)
                                    }
                                />
                            </Tooltip>}

                        </Space>
                    )}
                    {history.hash_with_winners && (
                        <Space align="start" direction="horizontal" size={4}>
                            <Text type="secondary" strong style={{ fontSize: 12 }}>Ganhadores:</Text>
                            <Tag color="green" style={{ marginLeft: 2 }}>
                                {history.hash_with_winners}
                            </Tag>
                            <Tooltip title="Copiar hash">
                                <Button
                                    icon={<FaCopy color="#999" />}
                                    size="small"
                                    onClick={() =>
                                        copyToClipboard(history.hash_with_winners!)
                                    }
                                />
                            </Tooltip>
                            {history.file_with_winners && <Tooltip title="Baixar arquivo de ganhadores">
                                <Button
                                    icon={<FaDownload />}
                                    size="small"
                                    onClick={() =>
                                        history.file_with_winners && handleClickDownloadTicketsFile(history.file_with_winners)
                                    }
                                />
                            </Tooltip>}
                        </Space>
                    )}
                </Space>)
            },
            showSorterTooltip: false,
        },
        {
            title: 'Ações',
            ...useActionsTable({
                showLabels: true,
                actions: [
                    {
                        type: EActionType.ENABLE,
                        icon: FaUsers,
                        label: 'Ganhadores',
                        onPress: (record: ITableData) => record.attempt_number && getWinnersByGiveawayAndAttempt(record.attempt_number),
                        hidden: (record: ITableData) => record.attempt_number === null || record.file_with_winners === null || record.observation !== 'Sorteio realizado!',
                    },
                ]
            }),
            width: '10%'
        },
    ]

    useEffect(() => {
        if (!id) {
            return;
        }

        const controller = new AbortController();

        loadGiveaway(controller.signal);
        loadRulesGiveaway(controller.signal);

        return () => {
            controller.abort();
        }
    }, [id, loadGiveaway, loadRulesGiveaway]);

    useEffect(() => {
        if (!giveaway || (!giveaway?.panel_user_generated_participants_file_id && !giveaway.panel_user_uploaded_winners_file_id)) {
            return;
        }

        const controller = new AbortController();

        async function loadPanelUsersData() {
            try {
                const userGeneratedParticipants = await PanelUsersService.getPanelUserById(giveaway?.panel_user_generated_participants_file_id!, controller.signal);

                setPanelUserGeneratedParticipantsFile(userGeneratedParticipants);

            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }

                EmitError(err);
            }
        }

        loadPanelUsersData();

        return () => {
            controller.abort();
        }
    }, [giveaway]);

    return {
        giveaway,
        isLoadingGiveaway,
        currentStep,
        handleClickExportStep,
        handleClickImportStep,
        handleClickNextStep,
        handleClickResultStep,
        handleClickPreviousStep,
        alreadyGeneratedTicketsFile,
        panelUserGeneratedParcitipantsFile,
        handleTicketsFileGenerated,
        enableResultStep,
        refreshGiveaway,
        tableData,
        handleClickViewAwards,
        columns,
        handleCloseModalWinners,
        showWinners,
        awardsWithWinners,
        lastGiveawayHistory,
        getOneWinnerByGiveawayAwardAndAttempt,
        rulesGiveaway
    }
}
